import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Users, TrendingUp, Calendar, UserCheck } from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { pt, enUS } from 'date-fns/locale';

const AdminStats = ({ contacts, appointments, language, dateFilter }) => {
  const locale = language === 'pt' ? pt : enUS;

  // Filter data by date range
  const filteredContacts = useMemo(() => {
    if (!dateFilter.startDate && !dateFilter.endDate) return contacts;
    
    return contacts.filter(contact => {
      const contactDate = new Date(contact.timestamp);
      const start = dateFilter.startDate ? new Date(dateFilter.startDate) : new Date('1970-01-01');
      const end = dateFilter.endDate ? new Date(dateFilter.endDate) : new Date('2100-01-01');
      end.setHours(23, 59, 59, 999);
      return contactDate >= start && contactDate <= end;
    });
  }, [contacts, dateFilter]);

  const filteredAppointments = useMemo(() => {
    if (!dateFilter.startDate && !dateFilter.endDate) return appointments;
    
    return appointments.filter(appt => {
      const apptDate = new Date(appt.created_at);
      const start = dateFilter.startDate ? new Date(dateFilter.startDate) : new Date('1970-01-01');
      const end = dateFilter.endDate ? new Date(dateFilter.endDate) : new Date('2100-01-01');
      end.setHours(23, 59, 59, 999);
      return apptDate >= start && apptDate <= end;
    });
  }, [appointments, dateFilter]);

  // Gender statistics
  const genderStats = useMemo(() => {
    const stats = { male: 0, female: 0, other: 0, unknown: 0 };
    filteredContacts.forEach(contact => {
      if (contact.gender === 'male') stats.male++;
      else if (contact.gender === 'female') stats.female++;
      else if (contact.gender === 'other') stats.other++;
      else stats.unknown++;
    });
    return [
      { name: language === 'pt' ? 'Masculino' : 'Male', value: stats.male, color: '#3B82F6' },
      { name: language === 'pt' ? 'Feminino' : 'Female', value: stats.female, color: '#EC4899' },
      { name: language === 'pt' ? 'Outro' : 'Other', value: stats.other, color: '#8B5CF6' },
      { name: language === 'pt' ? 'Não informado' : 'Not specified', value: stats.unknown, color: '#9CA3AF' },
    ].filter(item => item.value > 0);
  }, [filteredContacts, language]);

  // Monthly statistics
  const monthlyStats = useMemo(() => {
    const months = {};
    const now = new Date();
    
    // Initialize last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = format(date, 'yyyy-MM');
      months[key] = { 
        name: format(date, 'MMM', { locale }), 
        contacts: 0, 
        appointments: 0 
      };
    }
    
    // Count contacts
    contacts.forEach(contact => {
      const key = format(new Date(contact.timestamp), 'yyyy-MM');
      if (months[key]) months[key].contacts++;
    });
    
    // Count appointments
    appointments.forEach(appt => {
      const key = format(new Date(appt.created_at), 'yyyy-MM');
      if (months[key]) months[key].appointments++;
    });
    
    return Object.values(months);
  }, [contacts, appointments, locale]);

  // Status statistics
  const statusStats = useMemo(() => {
    const stats = { pending: 0, in_progress: 0, completed: 0 };
    filteredContacts.forEach(contact => {
      stats[contact.status] = (stats[contact.status] || 0) + 1;
    });
    return [
      { name: language === 'pt' ? 'Pendente' : 'Pending', value: stats.pending, color: '#F59E0B' },
      { name: language === 'pt' ? 'Em Andamento' : 'In Progress', value: stats.in_progress, color: '#3B82F6' },
      { name: language === 'pt' ? 'Concluído' : 'Completed', value: stats.completed, color: '#10B981' },
    ].filter(item => item.value > 0);
  }, [filteredContacts, language]);

  // Service type statistics
  const serviceStats = useMemo(() => {
    const stats = {};
    filteredContacts.forEach(contact => {
      const service = contact.service_type || 'Outro';
      stats[service] = (stats[service] || 0) + 1;
    });
    return Object.entries(stats)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [filteredContacts]);

  const totalClients = filteredContacts.length + filteredAppointments.length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs">{language === 'pt' ? 'Total de Pedidos' : 'Total Requests'}</p>
              <p className="text-3xl font-bold">{filteredContacts.length}</p>
            </div>
            <Users size={32} className="text-blue-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs">{language === 'pt' ? 'Agendamentos' : 'Appointments'}</p>
              <p className="text-3xl font-bold">{filteredAppointments.length}</p>
            </div>
            <Calendar size={32} className="text-green-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs">{language === 'pt' ? 'Concluídos' : 'Completed'}</p>
              <p className="text-3xl font-bold">
                {filteredContacts.filter(c => c.status === 'completed').length}
              </p>
            </div>
            <UserCheck size={32} className="text-purple-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-xs">{language === 'pt' ? 'Pendentes' : 'Pending'}</p>
              <p className="text-3xl font-bold">
                {filteredContacts.filter(c => c.status === 'pending').length}
              </p>
            </div>
            <TrendingUp size={32} className="text-orange-200" />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-heading font-semibold text-gray-900 mb-4">
            {language === 'pt' ? 'Distribuição por Género' : 'Gender Distribution'}
          </h3>
          {genderStats.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genderStats}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {genderStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              {language === 'pt' ? 'Sem dados disponíveis' : 'No data available'}
            </div>
          )}
        </div>

        {/* Monthly Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-heading font-semibold text-gray-900 mb-4">
            {language === 'pt' ? 'Clientes por Mês' : 'Clients per Month'}
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyStats}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar 
                  dataKey="contacts" 
                  name={language === 'pt' ? 'Orçamentos' : 'Quotes'} 
                  fill="#1B4D3E" 
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="appointments" 
                  name={language === 'pt' ? 'Agendamentos' : 'Appointments'} 
                  fill="#C9A227" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-heading font-semibold text-gray-900 mb-4">
            {language === 'pt' ? 'Status dos Pedidos' : 'Request Status'}
          </h3>
          {statusStats.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusStats}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {statusStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              {language === 'pt' ? 'Sem dados disponíveis' : 'No data available'}
            </div>
          )}
        </div>

        {/* Top Services */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-heading font-semibold text-gray-900 mb-4">
            {language === 'pt' ? 'Serviços Mais Solicitados' : 'Most Requested Services'}
          </h3>
          {serviceStats.length > 0 ? (
            <div className="space-y-3">
              {serviceStats.map((service, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-32 text-sm text-gray-600 truncate">{service.name}</div>
                  <div className="flex-1 mx-3">
                    <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${(service.value / serviceStats[0].value) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-8 text-sm font-semibold text-gray-900">{service.value}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              {language === 'pt' ? 'Sem dados disponíveis' : 'No data available'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
